/**
 * The customer-facing copy for a gifts-page product: the one-line spec under a
 * name, and the sentence + bullets behind "Details".
 *
 * ⚠️ THE TWO SOURCE COLUMNS ARE NOT RELIABLE, which is the whole reason this
 * exists. `description` and `tagline` are entered inconsistently across add-ons:
 * some hold the product spec, some the reverse, several still hold expired
 * coupon copy, and Coffee Cup's tagline is the literal string "coffeecup" — its
 * own css_name, which reached a live product page as a bullet point.
 *
 * TWO things code can recognise are rejected, and only two:
 *
 *   a slug   -- the value IS the product's own css_name ("coffeecup"), which
 *               reached a live product page as a bullet.
 *   an offer -- "20% OFF! Code: NEWYEAR", "50% OFF! Code: CYBER2025". Six live
 *               announcement rows carry one of these (2026-09-29), and this
 *               page promotes `description` to the line under the product's
 *               name, so an expired code was about to become HEADLINE COPY on a
 *               page that carries the real offer in a banner above it.
 *
 * The offer test is deliberately narrow -- a coupon code, or "N% off" -- and it
 * is not a guess at intent: a price claim is something the coupon system owns,
 * never something a product description gets to make. Anything subtler stays a
 * job for /admin/addons, as before.
 */

/**
 * Is this value the product's own css_name rather than something written for a
 * customer? Compared case- and space-insensitively so `Coffee Cup` vs
 * `coffeecup` is caught too.
 */
const isSlug = (value, cssName) => {
  if (!value || !cssName) return false;

  const normalise = (text) => String(text).toLowerCase().replace(/[\s_-]/g, '');

  return normalise(value) === normalise(cssName);
};

/**
 * Does this line make a price claim? A coupon code, or an "N% off". Narrow on
 * purpose -- see the note at the top of the file.
 */
const isOfferClaim = (value) => {
  const text = String(value || '');

  return /\bcode:\s*\S/i.test(text) || /\b\d{1,3}\s*%\s*off\b/i.test(text);
};

/** Every usable copy line a product holds, in a defined order, slug and offer claims removed. */
const copyLines = (addon) => [addon?.description, addon?.tagline]
  .filter(Boolean)
  .filter((text) => !isSlug(text, addon?.cssName))
  .filter((text) => !isOfferClaim(text))
  .flatMap((text) => String(text).split(/\r?\n/))
  .map((line) => line.trim())
  .filter(Boolean);

/**
 * The single spec line that sits under a product's name — "Set of 25
 * Announcements". Null when the record holds nothing usable, so the caller
 * renders no line rather than an empty one.
 */
export function specLine(addon) {
  // `detail_line` FIRST: it is the one field on /admin/addons whose meaning is
  // known -- the form labels it "One short line under the name on Announcements
  // & Gifts" -- whereas `description` holds whatever a season's staff put there.
  // It had no consumer at all until now, so the admin form was promising a
  // change that never appeared anywhere.
  const detail = String(addon?.detailLine || '').trim();
  if (detail) return detail;

  return copyLines(addon)[0] || null;
}

/**
 * "Details": the first line reads as the descriptive sentence, the rest as spec
 * bullets. That matches how staff already enter it — newlines are what separate
 * the facts ("Set of 25 Announcements\r\n5x7" Flat Card").
 */
export function detailCopy(addon) {
  const [lead = null, ...bullets] = copyLines(addon);

  return { lead, bullets };
}
